// src/sim/block.ts — Blok / parry dokunuşu / guard barı / guard break (DESIGN §3, §7; ARCHITECTURE §4.4 adım 1–2).
// Blok = L1 BASILI (guard 4 t'den; refrakterde 8 t'den); parry = L1 DOKUNMA (≤ 6 t'de bırakma, kalkanla ≤ 8) → bırakma tick'inden
// 6 t parry penceresi; pencere vuruşsuz kapanırsa 30 t refrakter (parry yok, blok var). Bloklu isabetten 7 t sonra tekrar bloklar.
// Guard 0 → break: 48 t sersem (Hitstun), bar 50'den, kalkan düşer. Blok yürüyüş hızı fighter.ts'de (×0.20). three'siz, tahsissiz.
//
// Block alt durumları: `blocking = true` → guard; `blocking = false` + stateUntil → 6 t çıkış (saldırı hemen başlayabilir: moves.ts).
// Parry başarısındaki 15 t serbest kalma kilidi `controlLockUntil` ile (hits.ts kurar; match.ts bu alanı yalnız geri sayım/eleme için yazar).
import { Btn, Ev, FState } from '../core/types.ts';
import type { Fighter, InputFrame, SimWorld } from '../core/types.ts';
import { NO_TICK } from '../config/constants.ts';
import { btnIndex } from '../config/controls.ts';
import { T } from '../config/tuning.ts';

/** Blok başlatılabilecek durumlar (yerde). */
function canEnterBlock(f: Fighter): boolean {
  return f.grounded && (f.state === FState.Idle || f.state === FState.Move || (f.state === FState.Block && !f.blocking));
}

/**
 * SimSystems.tryBlock: L1 basılıysa blok açar / tutar; bırakılınca dokunuş (parry penceresi) ya da çıkış. true → L1 bu tick bloğa gitti.
 * stepMoves her tick çağırır (saldırı tetikleri sonrasında; DESIGN §2 "blok tutarken X/Y/B" için önce saldırı bakılır).
 */
export function tryBlock(world: SimWorld, f: Fighter, input: InputFrame): boolean {
  const tick = world.tick;
  const held = (input.buttons & Btn.Block) !== 0;
  if (f.state === FState.Block && f.blocking) {
    if (held) return true;
    // bırakma tick'i: dokunuş mu, çıkış mı
    const heldTicks = tick - f.blockPressTick;
    const tap = f.hasShield ? T.BLOCK.shieldParryTapTicks : T.BLOCK.parryTapTicks;
    f.blocking = false;
    if (heldTicks <= tap && tick >= f.parryRefractoryUntil) {
      f.parryUntil = tick + T.BLOCK.parryWindowTicks;   // parry dokunuşu: bırakma tick'inden 6 t pencere
      f.state = FState.Idle; f.stateTick = tick; f.stateUntil = NO_TICK;
    } else {
      f.stateTick = tick; f.stateUntil = tick + T.BLOCK.exitTicks;   // 6 t çıkış (Block, blocking=false)
    }
    return false;
  }
  if (held && f.controlLockUntil <= tick && canEnterBlock(f)) {
    f.state = FState.Block; f.stateTick = tick; f.stateUntil = NO_TICK;
    f.blocking = true; f.blockSince = tick;
    const pt = world.buffers[f.id]!.pressTick[btnIndex(Btn.Block)]!;   // tampon basış tick'i (hitstop'ta yaşlanmaz: fighter.ts kaydırır)
    f.blockPressTick = pt !== NO_TICK && pt < tick ? pt : tick;
    return true;
  }
  return false;
}

/** Her tick (stepMoves başı): parry penceresi vuruşsuz kapanınca refrakter; blok çıkışı bitince Idle. */
export function stepBlock(world: SimWorld, f: Fighter): void {
  const tick = world.tick;
  if (f.parryUntil !== NO_TICK && tick >= f.parryUntil) {
    f.parryUntil = NO_TICK;
    f.parryRefractoryUntil = tick + T.BLOCK.parryRefractory;   // boşa dokunma → 30 t parry yok (blok var, guard 8 t'den)
  }
  if (f.state === FState.Block && !f.blocking && f.stateUntil !== NO_TICK && tick >= f.stateUntil) {
    f.state = FState.Idle; f.stateTick = tick; f.stateUntil = NO_TICK;
  }
}

/** Blok tutarken saldırıya geçiş (DESIGN §2): guard kapanır, çıkış atlanır, guard maliyeti yok. */
export function releaseBlockForAttack(f: Fighter): void {
  f.blocking = false;
}

/** Guard şu an mekanik olarak açık mı: Block ∧ blocking ∧ giriş süresi (4 t; refrakterde başladıysa 8 t) ∧ bloklu isabetten 7 t geçti. */
export function guardActive(f: Fighter, tick: number): boolean {
  if (f.state !== FState.Block || !f.blocking || f.guard <= 0) return false;
  const from = f.blockSince < f.parryRefractoryUntil ? T.BLOCK.refractoryGuardFrom : T.BLOCK.guardFrom;
  if (tick - f.blockSince < from) return false;
  if (f.guardLastHitTick !== NO_TICK && tick - f.guardLastHitTick < T.BLOCK.reblockTicks) return false;
  return true;
}

/** Gelen vuruş ön konide mi: dir.dirX/dirZ = saldıran → hedef yönü; koni 120° (kalkan 180°). Yön nesneyle gelir (hits.ts HIT): double
 *  alanları tagged argüman geçirmek V8'de HeapNumber kutular (sıcak yol tahsisi; düzeltici tur 2). */
export function inBlockCone(f: Fighter, dir: { dirX: number; dirZ: number }): boolean {
  const dirX = dir.dirX, dirZ = dir.dirZ;
  const cone = f.hasShield ? T.BLOCK.shieldConeDeg : T.BLOCK.coneDeg;
  const fx = Math.sin(f.yaw), fz = Math.cos(f.yaw);
  const dot = -(dirX * fx + dirZ * fz);   // saldırana bakış: −dir
  return dot >= Math.cos((cone * 0.5) * Math.PI / 180);
}

/** Parry penceresi açık mı (bırakma tick'inden 6 t). */
export const parryOpen = (f: Fighter, tick: number): boolean => f.parryUntil !== NO_TICK && tick < f.parryUntil;

/**
 * Bloklu isabet: guard −cost. Döner: guard break oldu mu. Break: 48 t Hitstun (Melee_Block_Hit tam), bar 50'den, kalkan düşer, Ev.GuardBreak;
 * blok çarpanları uygulanmaz (çağıran vuruşu bloksuz uygular). Aksi hâlde Ev.Blocked (v0 cost, v1 kalan guard) ve reblock 7 t.
 */
export function applyBlockedHit(world: SimWorld, b: Fighter, attacker: number, cost: number): boolean {
  const tick = world.tick;
  b.guard -= cost;
  b.guardLastHitTick = tick;
  if (b.guard <= 0) {
    b.guard = T.BLOCK.breakRestart;
    b.blocking = false;
    b.state = FState.Hitstun; b.stateTick = tick; b.stateUntil = tick + T.BLOCK.breakTicks;
    b.hitstunTicks = T.BLOCK.breakTicks;
    b.moveIdx = -1; b.phase = 0;
    world.events.push(Ev.GuardBreak, tick, attacker, b.id);
    if (b.hasShield) world.systems.items.drop(world, b, tick, Ev.GuardBreak);
    return true;
  }
  const e = world.events.push(Ev.Blocked, tick, attacker, b.id);
  e.v0 = cost; e.v1 = b.guard;
  return false;
}
